/**
 * Os valores que uma fórmula manipula, e a conversão entre eles.
 *
 * ## O que é "vazio", e por que isso é a decisão mais consequente do arquivo
 *
 * Numa tabela financeira a célula em branco é comum: lançamento sem data de pagamento, valor
 * ainda não informado, campo opcional. A pergunta é o que `prop("X") + 1` deve devolver
 * quando X está vazio.
 *
 * **A resposta aqui é `vazio`, e ele se propaga.** `vazio + 1` é `vazio`, não `1`. O motivo é
 * o mesmo da regra 8.9 do `CLAUDE.md`: tratar ausência como zero produz um número que parece
 * calculado e não é — e numa soma de coluna esse zero silencioso vira total errado com cara de
 * certo.
 *
 * Quem quiser o zero pede explicitamente: `coalesce(prop("X"), 0) + 1`. É uma tecla a mais e
 * uma dúvida a menos.
 *
 * ⚠️ A **exceção deliberada** é `concat`: texto vazio concatenado é string vazia, porque
 * `concat(prop("Nome"), " — ", prop("Obs"))` com observação em branco deve devolver o nome,
 * não vazio. Está declarada em `funcoes.ts` e não é regra geral.
 *
 * ## Data é número (epoch ms), como no upstream
 *
 * `t.date` é `defineDataType('Date', zod.number(), zod.number())` — o Affine já guarda data
 * como número. Não se inventa um tipo aqui: seria a segunda verdade da Regra #9.
 */

import { erro } from './erros';

export const VAZIO = Symbol('vazio');
export type Vazio = typeof VAZIO;

export type Valor = number | string | boolean | Vazio;

export const ehVazio = (v: unknown): v is Vazio => v === VAZIO;

/**
 * Normaliza o que veio de uma célula para um `Valor`.
 *
 * ⚠️ `null`, `undefined`, `''` e `NaN` são todos vazio. O `NaN` está na lista porque
 * `parseNumber` do upstream devolve `NaN` para entrada que não é número, e um `NaN` que
 * atravessasse o avaliador contaminaria toda conta a jusante **sem erro nenhum** — a
 * aritmética de `NaN` não lança, ela devolve `NaN`.
 */
export function normalizar(bruto: unknown): Valor {
  if (bruto === null || bruto === undefined) return VAZIO;
  if (typeof bruto === 'number') return Number.isFinite(bruto) ? bruto : VAZIO;
  if (typeof bruto === 'boolean') return bruto;
  if (typeof bruto === 'string') return bruto === '' ? VAZIO : bruto;
  if (Array.isArray(bruto)) return bruto.length === 0 ? VAZIO : bruto.join(', ');
  if (bruto instanceof Date) {
    const t = bruto.getTime();
    return Number.isFinite(t) ? t : VAZIO;
  }
  // Objeto que não sabemos ler vira o texto dele — nunca `[object Object]` silencioso.
  if (typeof bruto === 'object') {
    const comToString = bruto as { toString?: () => string };
    const texto = typeof comToString.toString === 'function' ? comToString.toString() : '';
    return texto && texto !== '[object Object]' ? texto : VAZIO;
  }
  return VAZIO;
}

export function comoNumero(v: Valor, onde: string): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string') {
    // Aceita o que o usuário digitaria numa célula de texto: "1.234,56" e "1234.56".
    const limpo = v.trim().replace(/\s/g, '');
    const emPtBr = /^-?\d{1,3}(\.\d{3})*(,\d+)?$/.test(limpo)
      ? limpo.replace(/\./g, '').replace(',', '.')
      : limpo.replace(',', '.');
    const n = Number(emPtBr);
    if (Number.isFinite(n)) return n;
    throw erro('tipo', `${onde}: "${v}" não é número`);
  }
  throw erro('tipo', `${onde}: esperava número e recebi vazio`);
}

export function comoTexto(v: Valor): string {
  if (ehVazio(v)) return '';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  return String(v);
}

export function comoBooleano(v: Valor, onde: string): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'sim' || s === 'verdadeiro') return true;
    if (s === 'false' || s === 'não' || s === 'nao' || s === 'falso') return false;
    return s !== '';
  }
  throw erro('tipo', `${onde}: esperava condição e recebi vazio`);
}

/**
 * Comparação entre dois valores.
 *
 * ⚠️ **Vazio só é igual a vazio, e nunca é maior nem menor que nada.** Ordenar contra vazio
 * devolveria uma resposta arbitrária que o usuário leria como fato — `prop("Pago em") >
 * today()` num lançamento não pago deve ser *não sei*, não *falso*. `!=` com vazio de um lado
 * segue respondendo, porque "é diferente de vazio" é pergunta legítima.
 */
export function comparar(op: string, a: Valor, b: Valor): Valor {
  const aVazio = ehVazio(a);
  const bVazio = ehVazio(b);

  if (op === '==') return aVazio || bVazio ? aVazio && bVazio : iguais(a, b);
  if (op === '!=') return aVazio || bVazio ? !(aVazio && bVazio) : !iguais(a, b);

  if (aVazio || bVazio) return VAZIO;

  const ordenavel = typeof a === 'number' || typeof b === 'number';
  const x = ordenavel ? comoNumero(a, `comparação "${op}"`) : comoTexto(a);
  const y = ordenavel ? comoNumero(b, `comparação "${op}"`) : comoTexto(b);

  switch (op) {
    case '>': return x > y;
    case '<': return x < y;
    case '>=': return x >= y;
    case '<=': return x <= y;
    default: throw erro('sintaxe', `operador desconhecido: "${op}"`);
  }
}

function iguais(a: Valor, b: Valor): boolean {
  if (typeof a === 'number' || typeof b === 'number') {
    try {
      return comoNumero(a, 'igualdade') === comoNumero(b, 'igualdade');
    } catch {
      // Número contra texto que não é número: não são iguais. Não é erro — é resposta.
      return false;
    }
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return comoBooleano(a, 'igualdade') === comoBooleano(b, 'igualdade');
  }
  return comoTexto(a) === comoTexto(b);
}
